import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, useRefreshPlan } from "@/lib/plan";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ phase: "checking" });
  const refresh = useRefreshPlan();
  const { setUser } = useAuth();
  useEffect(() => {
    const id = params.get("session_id");
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${id}`);
        if (data.payment_status === "paid" && data.plan.plan === "pro") {
          setState({ phase: "done", plan: data.plan });
          refresh();
          api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
          return;
        }
        if (data.status === "expired") return setState({ phase: "failed" });
      } catch {
        /* retry below */
      }
      if (!stop && ++tries < 15) setTimeout(poll, 2000);
      else if (!stop) setState({ phase: "slow" });
    };
    poll();
    return () => { stop = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sub = state.plan?.subscription;
  return (
    <div className="px-5 pt-16 text-center" data-testid="payment-success-page">
      {state.phase === "checking" && <><Loader2 className="mx-auto animate-spin text-[#E50914]" size={36} /><p className="mt-4 text-[#A1A1AA]">Confirming your subscription…</p></>}
      {state.phase === "done" && (
        <>
          <CheckCircle2 className="mx-auto text-[#22C55E]" size={44} />
          <h1 className="font-display text-2xl font-semibold mt-4" data-testid="payment-success-title">You're on Pro</h1>
          {sub?.status === "trialing" && <p className="text-sm text-[#A1A1AA] mt-2">Your free trial runs until {fmtDate(sub.trial_end)}. Cancel before then in Profile and you won't be charged.</p>}
          <Link to="/analytics" className="btn-primary mt-6" data-testid="payment-success-analytics">Open your analytics</Link>
        </>
      )}
      {state.phase === "slow" && <p className="text-[#A1A1AA]" data-testid="payment-slow">Payment received. It can take a minute to activate — check your Profile shortly.</p>}
      {state.phase === "failed" && <><XCircle className="mx-auto text-[#EF4444]" size={44} /><p className="mt-4 text-[#A1A1AA]">That checkout expired. Nothing was charged.</p><Link to="/pro" className="btn-secondary mt-6">Try again</Link></>}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="px-5 pt-16 text-center" data-testid="payment-cancel-page">
      <h1 className="font-display text-2xl font-semibold">Checkout cancelled</h1>
      <p className="text-sm text-[#A1A1AA] mt-2">Nothing was charged. You're still on your current plan.</p>
      <Link to="/pro" className="btn-secondary mt-6" data-testid="payment-cancel-back">Back to Pro</Link>
    </div>
  );
}
